import React, { useEffect, useState } from 'react';
import { studentsAPI, masterSettingsAPI } from '@/api/api';
import api from '@/api/api';
import { Search, Eye, Edit, Ban, MessageCircle, Copy, CheckCircle, FileSpreadsheet, FileText, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Pagination from '@/components/Pagination';
import { formatDate } from '../../utils/dateTimeUtils';

const StudentList = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [collegeInfo, setCollegeInfo] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchData();
    loadCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const loadCollegeInfo = async () => {
    try {
      const response = await api.get('/tenants/my-college/settings');
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, coursesRes, sectionsRes] = await Promise.all([
        api.get(`/students/?page=${currentPage}&limit=${itemsPerPage}`),
        masterSettingsAPI.getCourses(),
        masterSettingsAPI.getSections()
      ]);
      
      // Handle paginated response
      if (Array.isArray(studentsRes.data)) {
        setStudents(studentsRes.data);
        setTotalItems(studentsRes.pagination?.total || studentsRes.data.length);
      } else {
        setStudents(studentsRes.data?.data || []);
        setTotalItems(studentsRes.data?.total || 0);
      }
      
      setCourses(coursesRes.data);
      setSections(sectionsRes.data);
      
      // Fetch method subjects (used for filter dropdown)
      try {
        const msRes = await api.get('/master-settings/method-subjects');
        setMethodSubjects(Array.isArray(msRes.data) ? msRes.data : []);
      } catch (_) {
        setMethodSubjects([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleBlock = async (id) => {
    if (window.confirm('Are you sure you want to block this student?')) {
      try {
        await studentsAPI.blockStudent(id);
        fetchData();
      } catch (error) {
        console.error('Error blocking student:', error);
        alert('Failed to block student');
      }
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm('Are you sure you want to approve this student?')) {
      try {
        await studentsAPI.approveStudent(id);
        toast.success('Student approved successfully');
        fetchData();
      } catch (error) {
        console.error('Error approving student:', error);
        toast.error(error.response?.data?.detail || 'Failed to approve student');
      }
    }
  };

  const handleView = (studentId) => {
    navigate(`/students/details?id=${studentId}`);
  };

  const handleEdit = async (student) => {
    // Navigate to Student Admission form with student ID for editing
    // If student is approved (has admission confirmed), it will be read-only
    navigate(`/students/admission?edit=${student.id}`);
  };

  // Export to Excel - fetches all data
  const exportToExcel = async () => {
    try {
      toast.info('Fetching all data for export...');
      
      // Fetch all students without pagination for export
      const response = await api.get('/students/?limit=10000&include_all=false');
      const allStudents = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      if (allStudents.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const exportData = allStudents.map((student, index) => ({
        'S.No': index + 1,
        'Admission No': student.admission_number || '-',
        'College Roll No': student.roll_number || '-',
        'University ID': student.university_id || '-',
        'Method Subject': student.method_subject || '-',
        'Entry Type': student.is_lateral_entry ? `Lateral (Sem ${student.entry_semester || '-'})` : 'Regular',
        'Student Name': student.full_name || '-',
        'Father Name': student.father_name || '-',
        'Course': getCourseName(student.course_id),
        'Semester': student.semester || '-',
        'Email': student.email || '-',
        'Phone': student.phone || '-',
        'Status': student.status || '-',
        'Admission Date': student.admission_date ? formatDate(student.admission_date) : '-'
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Students');
      XLSX.writeFile(wb, `Student_List_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(`Exported ${allStudents.length} students to Excel`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export data');
    }
  };

  // Export to PDF - fetches all data
  const exportToPDF = async () => {
    try {
      toast.info('Fetching all data for export...');
      
      // Fetch all students without pagination for export
      const response = await api.get('/students/?limit=10000&include_all=false');
      const allStudents = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      if (allStudents.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
      
      // Title
      doc.setFontSize(16);
      doc.text('Student List', 14, 15);
      doc.setFontSize(10);
      doc.text(`Generated on: ${formatDate(new Date())} | Total: ${allStudents.length} students`, 14, 22);
      
      // Table data
      const tableData = allStudents.map((student, index) => [
        index + 1,
        student.admission_number || '-',
        student.roll_number || '-',
        student.university_id || '-',
        student.method_subject || '-',
        student.is_lateral_entry ? `Lateral${student.entry_semester ? ' (S' + student.entry_semester + ')' : ''}` : 'Regular',
        student.full_name || '-',
        student.father_name || '-',
        getCourseName(student.course_id),
        student.semester || '-',
        student.phone || '-',
        student.status || '-'
      ]);
      
      doc.autoTable({
        head: [['S.No', 'Adm. No', 'Roll No', 'Univ ID', 'Method Sub', 'Entry', 'Name', 'Father Name', 'Course', 'Sem', 'Phone', 'Status']],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [59, 130, 246] }
      });
      
      doc.save(`Student_List_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(`Exported ${allStudents.length} students to PDF`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export data');
    }
  };

  const [entryTypeFilter, setEntryTypeFilter] = useState('all'); // 'all' | 'regular' | 'lateral'
  const [methodSubjectFilter, setMethodSubjectFilter] = useState('all');
  const [methodSubjects, setMethodSubjects] = useState([]);

  const filteredStudents = students.filter(student => {
    const matchesSearch = (student.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.admission_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.roll_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.university_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (student.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMethod = methodSubjectFilter === 'all' || (student.method_subject || '') === methodSubjectFilter;
    
    if (!matchesSearch || !matchesMethod) return false;
    if (entryTypeFilter === 'lateral') return !!student.is_lateral_entry;
    if (entryTypeFilter === 'regular') return !student.is_lateral_entry;
    return true;
  });

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course ? course.name : '-';
  };

  // Send credentials to WhatsApp
  const sendToWhatsApp = (student) => {
    const phone = student.phone?.replace(/\D/g, '');
    if (!phone) {
      toast.error('Phone number not available');
      return;
    }
    
    const collegeName = collegeInfo?.college_name || collegeInfo?.name || 'Our College';
    
    // Get tenant-specific login link from current URL
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    const subdomain = pathParts[0]; // First part of path is subdomain (e.g., "hopeinstitute")
    const portalUrl = window.location.origin;
    
    // Build tenant-specific student portal login link
    // Correct path is /student/login (not /student-portal/login)
    const loginLink = subdomain && subdomain !== 'students' 
      ? `${portalUrl}/${subdomain}/student/login` 
      : `${portalUrl}/student/login`;
    
    const message = `🎓 *${collegeName}*

✅ *Admission Confirmed!*

📌 *Student ID:* ${student.admission_number || student.student_id}
📌 *Password:* ${student.phone}

🔗 *Student Portal Login:*
${loginLink}

📱 Download our app for easy access to your academic information.

Best Wishes! 🌟`;

    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Opening WhatsApp...');
  };

  // Copy credentials to clipboard
  const copyCredentials = (student) => {
    const text = `Student ID: ${student.admission_number || student.student_id}\nPassword: ${student.phone}`;
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard!');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="loading">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="student-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Student Management</h1>
          <p className="text-gray-600 mt-1">Manage all enrolled students</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export Buttons */}
          <Button
            variant="outline"
            onClick={exportToExcel}
            className="flex items-center gap-2"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet size={18} />
            Export Excel
          </Button>
          <Button
            variant="outline"
            onClick={exportToPDF}
            className="flex items-center gap-2"
            data-testid="export-pdf-btn"
          >
            <FileText size={18} />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <Input
              placeholder="Search by name, admission no, roll no, university ID, or email..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-students-input"
            />
          </div>
          <select
            value={entryTypeFilter}
            onChange={(e) => setEntryTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[160px]"
            data-testid="filter-entry-type"
          >
            <option value="all">All Entry Types</option>
            <option value="regular">🎓 Regular Only</option>
            <option value="lateral">🔀 Lateral Only</option>
          </select>
          {methodSubjects.length > 0 && (
            <select
              value={methodSubjectFilter}
              onChange={(e) => setMethodSubjectFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white min-w-[180px]"
              data-testid="filter-method-subject"
            >
              <option value="all">All Method Subjects</option>
              {methodSubjects.map(ms => (
                <option key={ms.id} value={ms.name}>{ms.name}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="students-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">College Roll No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">University ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Credentials</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    No students found. Add your first student to get started.
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50" data-testid={`student-row-${student.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {student.admission_number}
                      {student.is_lateral_entry && (
                        <span
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-300"
                          title={student.entry_semester ? `Lateral Entry (Sem ${student.entry_semester})` : 'Lateral Entry'}
                          data-testid={`lateral-badge-${student.id}`}
                        >
                          🔀 LATERAL{student.entry_semester ? ` · Sem ${student.entry_semester}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono" data-testid={`student-roll-${student.id}`}>
                      {student.roll_number || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-mono" data-testid={`student-univ-${student.id}`}>
                      {student.university_id || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" data-testid={`student-method-${student.id}`}>
                      {student.method_subject || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="bg-gray-50 px-2 py-1 rounded border">
                        <div className="text-sm font-bold text-green-600">ID: {student.admission_number || student.student_id}</div>
                        <div className="text-xs text-gray-500">Pass: {student.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{student.full_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{student.phone}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{getCourseName(student.course_id)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {student.is_approved ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">✅ Approved</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">⏳ Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        {!student.is_approved && (
                          <button 
                            onClick={() => handleApprove(student.id)}
                            className="text-green-600 hover:text-green-800 bg-green-50 p-1.5 rounded" 
                            title="Approve Student" 
                            data-testid={`approve-student-${student.id}`}
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        <button 
                          onClick={() => sendToWhatsApp(student)}
                          className="text-green-600 hover:text-green-800 bg-green-50 p-1.5 rounded" 
                          title="Send Credentials via WhatsApp" 
                        >
                          <MessageCircle size={16} />
                        </button>
                        <button 
                          onClick={() => copyCredentials(student)}
                          className="text-blue-600 hover:text-blue-800 bg-blue-50 p-1.5 rounded" 
                          title="Copy Credentials" 
                        >
                          <Copy size={16} />
                        </button>
                        <button 
                          onClick={() => handleView(student.id)}
                          className="text-purple-600 hover:text-purple-800" 
                          title="View Details" 
                          data-testid={`view-student-${student.id}`}
                        >
                          <Eye size={18} />
                        </button>
                        <button 
                          onClick={() => handleEdit(student)}
                          className="text-orange-600 hover:text-orange-800" 
                          title="Edit" 
                          data-testid={`edit-student-${student.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button 
                          onClick={() => handleBlock(student.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Block"
                          data-testid={`block-student-${student.id}`}
                        >
                          <Ban size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
          <p className="text-blue-600 text-sm font-medium">Total Students</p>
          <p className="text-3xl font-bold text-blue-700 mt-2" data-testid="total-students-stat">{totalItems}</p>
        </div>
        <div className="bg-green-50 p-6 rounded-xl border border-green-200">
          <p className="text-green-600 text-sm font-medium">On This Page</p>
          <p className="text-3xl font-bold text-green-700 mt-2">{students.length}</p>
        </div>
        <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
          <p className="text-purple-600 text-sm font-medium">Male (This Page)</p>
          <p className="text-3xl font-bold text-purple-700 mt-2">{students.filter(s => s.gender === 'Male').length}</p>
        </div>
        <div className="bg-pink-50 p-6 rounded-xl border border-pink-200">
          <p className="text-pink-600 text-sm font-medium">Female (This Page)</p>
          <p className="text-3xl font-bold text-pink-700 mt-2">{students.filter(s => s.gender === 'Female').length}</p>
        </div>
      </div>
    </div>
  );
};

export default StudentList;
